import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import SlotPicker from "../components/booking/SlotPicker";
import PatientForm, { EMPTY_FORM } from "../components/booking/PatientForm";

const STEPS = ["Consultation type", "Date & time", "Your details", "Payment"];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function Stepper({ step }) {
  return (
    <ol className="flex flex-wrap gap-3 sm:gap-6 mb-10" data-testid="booking-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-2 text-sm">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i < step ? "bg-teal-700 text-white" : i === step ? "bg-teal-50 text-teal-800 border border-teal-700" : "bg-slate-100 text-slate-500"}`}>
            {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
          </span>
          <span className={i === step ? "text-slate-900 font-medium" : "text-slate-500"}>{s}</span>
        </li>
      ))}
    </ol>
  );
}

function TypeStep({ type, setType, fees }) {
  const opts = [
    { v: "new", label: "New patient", fee: fees.new_fee, desc: "First consultation — diagnosis review, treatment options and sequencing." },
    { v: "followup", label: "Follow-up", fee: fees.followup_fee, desc: "For patients already under Dr. Porwal's care." },
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      {opts.map(o => (
        <button key={o.v} type="button" onClick={() => setType(o.v)} data-testid={`type-${o.v}-btn`}
          className={`text-left rounded-2xl border-2 p-6 transition-colors ${type === o.v ? "border-teal-700 bg-teal-50/50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <div className="flex justify-between items-baseline">
            <span className="font-semibold text-lg text-slate-900">{o.label}</span>
            <span className="font-serif text-2xl text-teal-800">₹{o.fee?.toLocaleString("en-IN")}</span>
          </div>
          <p className="mt-2 text-sm text-slate-600">{o.desc}</p>
          <p className="mt-3 text-xs text-slate-500">20-minute video consultation</p>
        </button>
      ))}
    </div>
  );
}

export default function Book() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [fees, setFees] = useState({ new_fee: 1800, followup_fee: 1500 });
  const [type, setType] = useState("new");
  const [slot, setSlot] = useState(null);
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata", []);
  const [form, setForm] = useState(EMPTY_FORM);
  const [paying, setPaying] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => { api.get("/settings").then(r => setFees(r.data)).catch(() => {}); }, []);

  const amount = type === "new" ? fees.new_fee : fees.followup_fee;

  const canNext = [true, !!slot, isFormValid(form), true][step];

  const pay = async () => {
    setPaying(true);
    try {
      const data = pending?.slot === slot.id && pending?.type === type ? pending.data
        : (await api.post("/bookings", { ...form, slot_id: slot.id, consultation_type: type, patient_timezone: tz })).data;
      setPending({ slot: slot.id, type, data });
      if (data.mock_mode) {
        const r = await api.post("/bookings/mock-pay", { booking_id: data.booking_id });
        toast.success("Booking confirmed");
        return nav(`/confirmation/${r.data.id}`);
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway");
      const rz = new window.Razorpay({
        key: data.key_id, amount: data.amount_paise, currency: "INR", order_id: data.razorpay_order_id,
        name: "Dr. Jasmine Porwal", description: `${type === "new" ? "New" : "Follow-up"} consultation`,
        prefill: { name: form.patient_name, email: form.patient_email, contact: form.patient_phone },
        theme: { color: "#0F766E" },
        handler: async (resp) => {
          try {
            const r = await api.post("/bookings/verify-payment", { booking_id: data.booking_id, ...resp });
            nav(`/confirmation/${r.data.id}`);
          } catch { toast.error("Payment verification failed. Please contact us."); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rz.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Something went wrong");
      setPaying(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-serif text-slate-900">Book your consultation</h1>
        <p className="mt-3 text-slate-600 mb-10">We're here to help you navigate your diagnosis with clear, evidence-backed options.</p>
        <Stepper step={step} />
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10">
          {step === 0 && <TypeStep type={type} setType={setType} fees={fees} />}
          {step === 1 && <SlotPicker tz={tz} selected={slot} onSelect={setSlot} />}
          {step === 2 && <PatientForm form={form} setForm={setForm} />}
          {step === 3 && <Summary type={type} slot={slot} tz={tz} form={form} amount={amount} />}
          <div className="mt-10 flex justify-between gap-4">
            <Button variant="outline" className="rounded-full px-6" disabled={step === 0 || paying} onClick={() => setStep(step - 1)} data-testid="booking-back-btn">Back</Button>
            {step < 3 ? (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="booking-next-btn">Continue</Button>
            ) : (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8" disabled={paying} onClick={pay} data-testid="booking-pay-btn">
                {paying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Pay ₹{amount?.toLocaleString("en-IN")} & confirm
              </Button>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}

function isFormValid(f) {
  return f.patient_name.trim() && f.patient_dob && f.patient_gender && f.patient_phone.trim().length >= 7 &&
    /\S+@\S+\.\S+/.test(f.patient_email) && f.patient_country.trim() && f.patient_city.trim() && f.reason.trim() && f.consent;
}

function Summary({ type, slot, tz, form, amount }) {
  const when = new Date(slot.start_utc).toLocaleString("en-IN", { timeZone: tz, dateStyle: "full", timeStyle: "short" });
  const rows = [
    ["Consultation", type === "new" ? "New patient" : "Follow-up"],
    ["When", `${when} (${tz})`],
    ["IST", `${slot.ist_date} · ${slot.ist_time}`],
    ["Patient", form.patient_name],
    ["Email", form.patient_email],
    ["Phone", form.patient_phone],
  ];
  return (
    <div data-testid="booking-summary">
      <h2 className="text-xl font-serif text-slate-900 mb-6">Review & pay</h2>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="py-3 grid grid-cols-3 gap-4 text-sm"><dt className="text-slate-500">{k}</dt><dd className="col-span-2 text-slate-900">{v}</dd></div>
        ))}
        <div className="py-4 grid grid-cols-3 gap-4"><dt className="text-slate-500">Total</dt><dd className="col-span-2 font-serif text-2xl text-teal-800" data-testid="summary-total">₹{amount?.toLocaleString("en-IN")}</dd></div>
      </dl>
      <p className="mt-4 text-xs text-slate-500">Full refund if cancelled 24+ hours before your appointment. Payments processed securely by Razorpay.</p>
    </div>
  );
}
